// src/pages/Donate.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import DonationForm from '../components/Forms/DonationForm';

const Donate: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Donate - Impact Generation Africa</title>
        <meta 
          name="description" 
          content="Support our mission of creating sustainable food security and climate resilience in African communities. Your donation makes a difference." 
        />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="py-5 bg-success text-white"
        style={{ marginTop: '76px' }}
      >
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <motion.h1 
                className="display-5 fw-bold mb-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                Make a Difference
              </motion.h1>
              <motion.p 
                className="lead mb-0"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Your donation supports sustainable food security, climate resilience, and community empowerment across Africa
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-5">
        <div className="container">
          <div className="row text-center">
            <motion.div 
              className="col-md-3 col-6 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-success h2 fw-bold">15,000+</div>
              <p className="text-muted mb-0">Trees Planted</p>
            </motion.div>
            <motion.div 
              className="col-md-3 col-6 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-success h2 fw-bold">500+</div>
              <p className="text-muted mb-0">Women Empowered</p>
            </motion.div>
            <motion.div 
              className="col-md-3 col-6 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-success h2 fw-bold">25+</div>
              <p className="text-muted mb-0">Communities Reached</p>
            </motion.div>
            <motion.div 
              className="col-md-3 col-6 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-success h2 fw-bold">200+</div>
              <p className="text-muted mb-0">Volunteers Engaged</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-5 bg-light">
        <div className="container">
          <DonationForm />
        </div>
      </section>

      {/* Other Ways to Support */}
      <section className="py-5">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-12">
              <motion.h2 
                className="display-6 fw-bold text-success mb-3"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                Other Ways to Support
              </motion.h2>
            </div>
          </div>
          <div className="row">
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="card border-0 shadow-sm h-100 text-center">
                <div className="card-body p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>👥</div>
                  <h5 className="card-title text-success">Become a Volunteer</h5>
                  <p className="card-text text-muted">
                    Share your skills and time to make a direct impact in our community projects.
                  </p>
                  <a href="/volunteer" className="btn btn-outline-success">Learn More</a>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="card border-0 shadow-sm h-100 text-center">
                <div className="card-body p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>🤝</div>
                  <h5 className="card-title text-success">Corporate Partnership</h5>
                  <p className="card-text text-muted">
                    Partner with us for CSR initiatives, employee engagement, and sustainable impact.
                  </p>
                  <a href="/contact" className="btn btn-outline-success">Partner With Us</a>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="col-md-4 mb-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="card border-0 shadow-sm h-100 text-center">
                <div className="card-body p-4">
                  <div className="text-success mb-3" style={{ fontSize: '3rem' }}>📢</div>
                  <h5 className="card-title text-success">Spread the Word</h5>
                  <p className="card-text text-muted">
                    Help us reach more people by sharing our mission with your network.
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <a href="#" className="btn btn-outline-primary btn-sm">
                      <i className="bi bi-facebook"></i>
                    </a>
                    <a href="#" className="btn btn-outline-info btn-sm">
                      <i className="bi bi-twitter"></i>
                    </a>
                    <a href="#" className="btn btn-outline-danger btn-sm">
                      <i className="bi bi-instagram"></i>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Donate;